import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { Content, Header, Page, Progress } from '@backstage/core-components';
import { useApi, fetchApiRef, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { Link as RouterLink } from 'react-router-dom';
import { useAdminGroupMember } from '../devtools/AdminGroupMemberContext';

const BACKSTAGE_USER_EMAIL_HEADER = 'X-Backstage-User-Email';

const OPERATOR_WRONG_HINT =
  'Please provide a valid request number, or sign in with an administrator account to use this lookup.';

async function waitForIdentity(
  identityApi: { getProfileInfo: () => Promise<{ email?: string }> },
  maxAttempts = 4,
  delayMs = 400,
): Promise<void> {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      await identityApi.getProfileInfo();
      return;
    } catch {
      if (i < maxAttempts - 1) {
        await new Promise(r => setTimeout(r, delayMs));
      }
    }
  }
}

async function fetchWithRetryOn401(
  fetchApi: { fetch: (url: string, init?: { headers?: Record<string, string> }) => Promise<Response> },
  url: string,
  headers: Record<string, string> | undefined,
  retryDelayMs = 600,
): Promise<Response> {
  let res = await fetchApi.fetch(url, headers ? { headers } : undefined);
  if (res.status === 401 && retryDelayMs > 0) {
    await new Promise(r => setTimeout(r, retryDelayMs));
    res = await fetchApi.fetch(url, headers ? { headers } : undefined);
  }
  return res;
}

function TicketLogsLookupPanel({
  fetchApi,
  config,
  identityApi,
  authReady,
}: {
  fetchApi: { fetch: (url: string, init?: { headers?: Record<string, string> }) => Promise<Response> };
  config: { getString: (key: string) => string };
  identityApi: { getProfileInfo: () => Promise<{ email?: string }> };
  authReady: boolean;
}) {
  const [requestNo, setRequestNo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const runLookup = useCallback(async () => {
    setError(null);
    setTaskId(null);
    setInfoMessage(null);
    const trimmed = requestNo.trim();
    if (!/^\d+$/.test(trimmed)) {
      setError(`Invalid request number. ${OPERATOR_WRONG_HINT}`);
      return;
    }
    if (!authReady) return;
    setLoading(true);
    try {
      const baseUrl = config.getString('backend.baseUrl');
      const url = `${baseUrl}/api/requests/requests/operator/scaffolder-task/${trimmed}`;
      const headers: Record<string, string> = {};
      try {
        const profile = await identityApi.getProfileInfo();
        const email = profile.email?.trim();
        if (email && email.includes('@')) headers[BACKSTAGE_USER_EMAIL_HEADER] = email;
      } catch {
        /* ignore */
      }
      const res = await fetchWithRetryOn401(
        fetchApi,
        url,
        Object.keys(headers).length > 0 ? headers : undefined,
      );
      const body = await res.json().catch(() => ({}));
      if (res.status === 401) {
        setError(`Sign in required. ${OPERATOR_WRONG_HINT}`);
        return;
      }
      if (res.status === 400 || res.status === 404) {
        setError(
          typeof body.error === 'string' && body.error.trim()
            ? `${body.error} ${OPERATOR_WRONG_HINT}`
            : OPERATOR_WRONG_HINT,
        );
        return;
      }
      if (res.status === 403) {
        setError(
          typeof body.error === 'string' && body.error.trim()
            ? `${body.error} ${OPERATOR_WRONG_HINT}`
            : OPERATOR_WRONG_HINT,
        );
        return;
      }
      if (!res.ok) {
        setError(
          typeof body.error === 'string' && body.error.trim()
            ? body.error
            : `Request failed (${res.status}). ${OPERATOR_WRONG_HINT}`,
        );
        return;
      }
      if (body.scaffolder_task_id && typeof body.scaffolder_task_id === 'string') {
        setTaskId(body.scaffolder_task_id.trim());
        return;
      }
      if (typeof body.message === 'string' && body.message.trim()) {
        setInfoMessage(body.message.trim());
        return;
      }
      setInfoMessage(`No scaffolder task on file for request ${body.request_id ?? trimmed}.`);
    } catch {
      setError(OPERATOR_WRONG_HINT);
    } finally {
      setLoading(false);
    }
  }, [requestNo, authReady, config, fetchApi, identityApi]);

  return (
    <Paper style={{ padding: 24, maxWidth: 560 }}>
      <Typography variant="h6" gutterBottom>
        Look up scaffolder log
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Enter a request number from Firestore or the Requests list. When a task id is stored for that
        ticket, you can open the scaffolder execution log (admin only).
      </Typography>
      <Box display="flex" flexDirection="row" alignItems="flex-start" flexWrap="wrap" style={{ gap: 12 }}>
        <TextField
          label="Request number"
          variant="outlined"
          size="small"
          value={requestNo}
          onChange={e => setRequestNo(e.target.value.replace(/\D/g, ''))}
          placeholder="e.g. 10042"
          inputProps={{ inputMode: 'numeric', 'aria-label': 'Request number' }}
        />
        <Button variant="contained" color="primary" disabled={loading} onClick={runLookup}>
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Look up'}
        </Button>
      </Box>
      {error ? (
        <Box mt={2}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        </Box>
      ) : null}
      {infoMessage ? (
        <Box mt={2}>
          <Typography color="textSecondary" variant="body2">
            {infoMessage}
          </Typography>
        </Box>
      ) : null}
      {taskId ? (
        <Box mt={2}>
          <Typography variant="body2" gutterBottom>
            Task ID: <code>{taskId}</code>
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            component={RouterLink}
            to={`/create/tasks/${taskId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open scaffolder execution log
          </Button>
        </Box>
      ) : null}
    </Paper>
  );
}

/**
 * Admin-only page linked from the sidebar (“Ticket logs”). Not a tab on Requests.
 */
export const TicketLogsPage = () => {
  const fetchApi = useApi(fetchApiRef);
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const adminMember = useAdminGroupMember();
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    waitForIdentity(identityApi).then(() => {
      if (!cancelled) setAuthReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [identityApi]);

  const allowed = adminMember === true;
  const checkingAccess = adminMember === null || !authReady;

  if (checkingAccess) {
    return (
      <Page themeId="home">
        <Header title="Ticket logs" subtitle="Admin tools" />
        <Content>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <Progress />
          </Box>
        </Content>
      </Page>
    );
  }

  if (!allowed) {
    return (
      <Page themeId="home">
        <Header title="Ticket logs" subtitle="Admin tools" />
        <Content>
          <Box p={3} maxWidth={560}>
            <Typography variant="body1" paragraph>
              You do not have access to this page. It is limited to administrators (same group as DevTools /
              catalog admin checks).
            </Typography>
            <Button component={RouterLink} to="/requests" variant="outlined" color="primary">
              Back to Requests
            </Button>
          </Box>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="home">
      <Header title="Ticket logs" subtitle="Look up scaffolder execution by request number" />
      <Content>
        <Box pt={2}>
          <TicketLogsLookupPanel
            fetchApi={fetchApi}
            config={config}
            identityApi={identityApi}
            authReady={authReady}
          />
          <Box mt={2}>
            <Button component={RouterLink} to="/requests" color="primary">
              Back to Requests
            </Button>
          </Box>
        </Box>
      </Content>
    </Page>
  );
};
